import {
  dropsOperationLevel,
  stationCommunicationSystemsEnum,
  stationCustomEventSeverityEnum,
  stationMeansOfPaymentEnum,
  stationThreatLevelEnum,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stationEvents/types/formSchema";
import {
  distributionInput,
  distributionTexts,
  stopIdSchema,
} from "features/CenterContent/shared/operationalInformation/utils";
import { visibilitySchemaResponse } from "shared/form/visibilitySchema";
import { z } from "zod";

const stationThreatLevelSchema = z.object({
  stationInformationType: z.literal("STATION_THREAT_LEVEL"),
  stops: stopIdSchema.array().min(1),
  threatLevel: stationThreatLevelEnum,
});

const stationSalesChannelReducedSchema = z.object({
  stationInformationType: z.literal("STATION_SALES_CHANNEL_REDUCED"),
  stops: stopIdSchema.array().min(1),
  meansOfPayment: stationMeansOfPaymentEnum,
});

const stationPersonSchema = z.object({
  stationInformationType: z.enum([
    "STATION_DIFFICULT_CUSTOMER",
    "STATION_WANTED_PERSON",
  ]),
  stops: stopIdSchema.array().min(1),
  personDescription: distributionTexts,
});

const stationSingleStopSchema = z.object({
  stationInformationType: z.enum([
    "STATION_ARRIVAL_AND_DEPARTURE_MONITOR_FAULT",
    "STATION_ELEVATOR_FAULT",
    "STATION_SPEAKER_FAULT",
    "STATION_TRACK_MONITOR_FAULT",
    "STATION_WHEELCHAIR_RAMP_FAULT",
  ]),
  stop: stopIdSchema,
  affectedTracks: z.number().int().nonnegative().array(),
});

const stationCommunicationSystemsDownSchema = z.object({
  stationInformationType: z.literal("STATION_COMMUNICATION_SYSTEMS_DOWN"),
  stops: stopIdSchema.array().min(1),
  affectedSystems: stationCommunicationSystemsEnum.array().min(1),
});

const stationDropsOperationReducedSchema = z.object({
  stationInformationType: z.literal("STATION_DROPS_OPERATION"),
  stops: stopIdSchema.array().min(1),
  level: dropsOperationLevel,
});

const stationCustomSchema = z.object({
  stationInformationType: z.literal("STATION_GENERAL"),
  stops: stopIdSchema.array().min(1),
  severity: stationCustomEventSeverityEnum,
  distributions: distributionInput,
});

const stationGeneralSchema = z.object({
  stationInformationType: z.enum([
    "STATION_PLATFORM_FAULT",
    "STATION_ALL_SALES_CHANNELS_DOWN",
    "STATION_APP_SALES_CHANNEL_DOWN",
    "STATION_MOBILE_TERMINALS_SALES_CHANNEL_DOWN",
    "STATION_TICKET_MACHINE_DOWN",
    "STATION_TICKET_VALIDATOR_DOWN",
    "STATION_DEPOSIT_MACHINE_DOWN",
    "STATION_MAIN_MONITOR_FAULT",
    "STATION_PLATFORM_SLIPPERY",
    "STATION_PARKING_ISSUE",
    "STATION_ENTRANCE_CHANGED",
    "STATION_WAITING_ROOM_CLOSED",
    "STATION_CLOSED_FOR_PASSENGERS",
    "STATION_DROPS_EVACUATION",
  ]),
  stops: stopIdSchema.array().min(1),
});

export const stationFormSchema = z.discriminatedUnion(
  "stationInformationType",
  [
    stationThreatLevelSchema,
    stationSalesChannelReducedSchema,
    stationPersonSchema,
    stationSingleStopSchema,
    stationCommunicationSystemsDownSchema,
    stationDropsOperationReducedSchema,
    stationCustomSchema,
    stationGeneralSchema,
  ],
);

export const stationEventResponse = stationFormSchema.and(
  z.object({
    message: z.string().nullable(),
    uuid: z.string(),
    visibility: visibilitySchemaResponse,
    countryCode: z.enum(["NO", "SE"]),
  }),
);
export type StationEventResponse = z.infer<typeof stationEventResponse>;

export const getAffectedStops = (stationEvent: StationEventResponse) => {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- single special case
  switch (stationEvent.stationInformationType) {
    case "STATION_ARRIVAL_AND_DEPARTURE_MONITOR_FAULT":
    case "STATION_ELEVATOR_FAULT":
    case "STATION_SPEAKER_FAULT":
    case "STATION_TRACK_MONITOR_FAULT":
    case "STATION_WHEELCHAIR_RAMP_FAULT":
      return stationEvent.stop.name ?? stationEvent.stop.id;
    default:
      return stationEvent.stops.length > 400
        ? "Hele landet"
        : stationEvent.stops.map((stop) => stop.name ?? stop.id).join(", ");
  }
};
